import React, { useState } from 'react';
import {
  Button,
  useColorModeValue,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import api from '../api';

const PrintProductsList = ({ filters }) => {
  const [isLoading, setIsLoading] = useState(false);
  
  // Custom Print Icon component
  const PrintIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
      <path
        fill="currentColor"
        d="M19,8H18V3H6V8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8M8,5H16V8H8V5M16,19H8V15H16V19M20,15H18V13H6V15H4V11A1,1 0 0,1 5,10H19A1,1 0 0,1 20,11V15Z"
      />
    </Icon>
  );

  const fetchAllProducts = async () => {
    let url = '/products/export/';
    
    // Apply current filters to the API call
    const params = new URLSearchParams();
    if (filters?.name) params.append('search', filters.name);
    if (filters?.minPrice) params.append('min_price', filters.minPrice);
    if (filters?.maxPrice) params.append('max_price', filters.maxPrice);
    if (filters?.isVat) params.append('is_vat', filters.isVat);
    if (filters?.selectedCategories?.length) {
      params.append(
        'categories',
        filters.selectedCategories.map((cat) => cat.id).join(',')
      );
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    try {
      const response = await api.get(url);
      // The new endpoint already returns products sorted by ID
      return response.data.results || [];
    } catch (error) {
      console.error('Failed to fetch all products:', error);
      return [];
    }
  };

  const handlePrint = async () => {
    setIsLoading(true);
    
    try {
      // Fetch all products with current filters using the export endpoint
      const products = await fetchAllProducts();
      
      if (products.length === 0) {
        alert('Немає товарів для друку');
        return;
      }

      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      // Get current date for the header
      const currentDate = new Date().toLocaleDateString('uk-UA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      
      const currentTime = new Date().toLocaleTimeString('uk-UA', {
        hour: '2-digit',
        minute: '2-digit',
      });

      // Generate the HTML content for printing
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Список товарів</title>
            <meta charset="UTF-8">
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
                color: #333;
              }
              
              .header {
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 2px solid #4A5568;
                padding-bottom: 15px;
              }
              
              .header h1 {
                font-size: 24px;
                margin: 0;
                color: #2D3748;
              }
              
              .header .date-info {
                font-size: 14px;
                color: #718096;
                margin-top: 10px;
              }
              
              .products-table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
                font-size: 12px;
              }
              
              .products-table th,
              .products-table td {
                border: 1px solid #E2E8F0;
                padding: 8px;
                text-align: left;
                vertical-align: top;
              }
              
              .products-table th {
                background-color: #F7FAFC;
                font-weight: bold;
                color: #2D3748;
                font-size: 11px;
              }
              
              .products-table tr:nth-child(even) {
                background-color: #F9F9F9;
              }
              
              .products-table tr:hover {
                background-color: #EDF2F7;
              }
              
              .id-column {
                width: 60px;
                text-align: center;
              }
              
              .catalog-column {
                width: 120px;
              }
              
              .name-column {
                min-width: 200px;
                max-width: 300px;
                word-wrap: break-word;
              }
              
              .unit-column {
                width: 100px;
                text-align: center;
              }
              
              .quantity-column {
                width: 80px;
                text-align: center;
              }
              
              .country-column {
                width: 120px;
              }
              
              .footer {
                margin-top: 30px;
                text-align: center;
                font-size: 12px;
                color: #718096;
                border-top: 1px solid #E2E8F0;
                padding-top: 15px;
              }
              
              @media print {
                body {
                  margin: 0;
                  padding: 15px;
                }
                
                .header {
                  margin-bottom: 20px;
                  padding-bottom: 10px;
                }
                
                .products-table {
                  font-size: 11px;
                }
                
                .products-table th,
                .products-table td {
                  padding: 6px;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Список товарів</h1>
              <div class="date-info">
                <div>Дата: ${currentDate}</div>
                <div>Час: ${currentTime}</div>
                <div>Кількість товарів: ${products.length}</div>
              </div>
            </div>
            
            <table class="products-table">
              <thead>
                <tr>
                  <th class="id-column">ID</th>
                  <th class="catalog-column">Каталожний номер</th>
                  <th class="name-column">Назва товару</th>
                  <th class="unit-column">Одиниця вимірювання</th>
                  <th class="quantity-column">Кількість</th>
                  <th class="country-column">Країна виробник</th>
                  <th class="price-column">Ціна</th>
                </tr>
              </thead>
              <tbody>
                ${products.map(product => `
                  <tr>
                    <td class="id-column">${product.id}</td>
                    <td class="catalog-column">${product.catalog_number || '-'}</td>
                    <td class="name-column">${product.name}</td>
                    <td class="unit-column">${product.unit_measurement || '-'}</td>
                    <td class="quantity-column">${product.stock_quantity || '0'}</td>
                    <td class="country-column">${product.country_of_manufacture || '-'}</td>
                    <td class="price-column">${product.price || '0'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="footer">
              <div>Згенеровано системою управління товарами</div>
              <div>Дата та час створення: ${currentDate} ${currentTime}</div>
            </div>
          </body>
        </html>
      `;

      // Write content to the print window
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Wait for the content to load, then print
      printWindow.onload = () => {
        printWindow.print();
        // Close the window after printing (optional)
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      };
    } catch (error) {
      console.error('Error printing products:', error);
      alert('Помилка при друку списку товарів');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button 
      bg="linear-gradient(135deg, #38B2AC, #319795)"
      color="white"
      size="lg"
      leftIcon={isLoading ? <Spinner size="sm" /> : <PrintIcon />}
      onClick={handlePrint}
      borderRadius="xl"
      px={8}
      boxShadow="0 4px 15px rgba(56, 178, 172, 0.4)"
      _hover={{
        bg: "linear-gradient(135deg, #319795, #2C7A7B)",
        transform: "translateY(-2px)",
        boxShadow: "0 8px 25px rgba(56, 178, 172, 0.6)",
        transition: "all 0.2s ease"
      }}
      _active={{
        transform: "translateY(0)",
      }}
      isLoading={isLoading}
      loadingText="Завантаження..."
      isDisabled={isLoading}
    >
      {isLoading ? 'Завантаження...' : '🖨️ Друкувати список'}
    </Button>
  );
};

export default PrintProductsList;
